import React, { useState, useEffect } from "react";
import { Layout, Table, Button, Space, Descriptions, Card, Tag, message, Spin, Tooltip, Timeline } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { ArrowLeftOutlined, EyeOutlined, PrinterOutlined, DownloadOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, SendOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";
import { getStatusColor } from "../../../utils/statusHelper";
import IssueNotePreviewModal from "../../../components/issuing/IssueNotePreviewModal";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";

const { Content } = Layout;

const ViewIssueNote = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [note, setNote] = useState(null);
    const [items, setItems] = useState([]);
    const [previewVisible, setPreviewVisible] = useState(false);

    useEffect(() => {
        fetchNoteDetails();
    }, [id]);

    const fetchNoteDetails = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/inventory/issuing/list.php?iin_id=${id}`);
            if (response.data.status === "success" && response.data.dataset) {
                setNote(response.data.dataset);
                setItems(response.data.dataset.items || []);
            } else {
                message.error("Material Issue Note not found.");
            }
        } catch (error) {
            message.error("Failed to load Material Issue Note details.");
        } finally {
            setLoading(false);
        }
    };



    const getTimelineItems = () => {
        if (!note) return [];
        const rawItems = [];
        const status = note.iin_status;

        // Step 1: Prepared
        rawItems.push({
            statusName: 'Issue Note Prepared',
            statusLabel: 'Prepared',
            color: '#faad14',
            dotIcon: <ClockCircleOutlined style={{ color: '#faad14' }} />,
            by: note.prepare_user_dname,
            date: note.prepare_dt ? dayjs(note.prepare_dt).format("YYYY-MM-DD hh:mm A") : '',
            comment: null
        });

        // Step 2: Approved / Ready
        if (note.completed_dt || note.complete_user_dname) {
            rawItems.push({
                statusName: 'Approved (Ready for Issue)',
                statusLabel: 'Ready',
                color: '#52c41a',
                dotIcon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                by: note.complete_user_dname,
                date: note.completed_dt ? dayjs(note.completed_dt).format("YYYY-MM-DD hh:mm A") : '',
                comment: null
            });
        }

        // Step 3: Issued / Collected
        if (note.collected_dt || note.collect_user_dname) {
            rawItems.push({
                statusName: 'Items Collected / Issued',
                statusLabel: 'Issued',
                color: '#2d5a3d',
                dotIcon: <CheckCircleOutlined style={{ color: '#2d5a3d' }} />,
                by: note.collect_user_dname,
                date: note.collected_dt ? dayjs(note.collected_dt).format("YYYY-MM-DD hh:mm A") : '',
                comment: null
            });
        }

        const items = rawItems.map((item, idx) => {
            const stepNum = idx + 1;
            return {
                color: item.color,
                dot: (
                    <div style={{
                        padding: '4px',
                        background: item.color === '#52c41a' || item.color === '#2d5a3d' ? '#f6ffed' : '#fffbe6',
                        borderRadius: '50%',
                        border: `1px solid ${item.color}`
                    }}>
                        {item.dotIcon}
                    </div>
                ),
                children: (
                    <div style={{
                        background: '#f9f9f9',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${item.color}`
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>{item.statusName}</span>
                            <Tag color={getStatusColor(item.statusLabel)}>Step {stepNum}</Tag>
                        </div>
                        {item.by && (
                            <div style={{ fontSize: '13px', color: '#555' }}>
                                By: <span style={{ fontWeight: 500 }}>{item.by}</span>
                            </div>
                        )}
                        {item.date && (
                            <div style={{ fontSize: '12px', color: '#888' }}>
                                {item.date}
                            </div>
                        )}
                    </div>
                )
            };
        });

        return items.reverse();
    };

    const renderStatusTag = (status) => {
        return (
            <Tag color={getStatusColor(status)} style={{ fontWeight: 600 }}>
                {status}
            </Tag>
        );
    };

    const columns = [
        {
            title: "Item Code",
            dataIndex: "in_code",
            key: "in_code"
        },
        {
            title: "Item Name",
            dataIndex: "in_name",
            key: "in_name",
            render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
        },
        {
            title: "UOM",
            dataIndex: "uom_name",
            key: "uom_name",
            render: (text) => text || "-"
        },
        {
            title: "Issue Qty",
            dataIndex: "issuenote_qty",
            key: "issuenote_qty",
            align: 'right',
            render: (qty) => <strong>{parseFloat(qty || 0).toFixed(2)}</strong>
        },
        {
            title: "Serial Numbers",
            dataIndex: "serial_numbers",
            key: "serial_numbers",
            render: (text) => text || "-"
        },
        {
            title: "Special Details",
            dataIndex: "special_details",
            key: "special_details",
            render: (text) => text || "-"
        }
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'View Issue Note' },
                ]} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 className="fluid-page-title" style={{ margin: 0 }}>
                            <EyeOutlined style={{ marginRight: 12 }} />
                            Issue Note: {note ? note.iin_ref : "Loading..."}
                        </h1>
                        
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {note && (
                            <Tooltip title="Print">
                                <Button
                                    icon={<PrinterOutlined />}
                                    onClick={() => setPreviewVisible(true)}
                                    className="fluid-btn-white"
                                />
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>

            <Content className="fluid-content" style={{ minHeight: "calc(100vh - 350px)" }}>
                {loading || !note ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin size="large" tip="Loading Issue Note Details..." />
                    </div>
                ) : (
                    <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
                        <Card className="fluid-card" title="Issue Note Overview">
                            <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} size="small">
                                <Descriptions.Item label="Reference"><strong>{note.iin_ref}</strong></Descriptions.Item>
                                <Descriptions.Item label="Status">{renderStatusTag(note.iin_status)}</Descriptions.Item>
                                <Descriptions.Item label="MR Ref">{note.mr_ref}</Descriptions.Item>
                                <Descriptions.Item label="Prepared By">{note.prepare_user_dname || "-"}</Descriptions.Item>
                                <Descriptions.Item label="Prepared Date">
                                    {note.prepare_dt ? dayjs(note.prepare_dt).format("YYYY-MM-DD hh:mm A") : "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Approved By">{note.complete_user_dname || "-"}</Descriptions.Item>
                                <Descriptions.Item label="Approved Date">
                                    {note.completed_dt ? dayjs(note.completed_dt).format("YYYY-MM-DD hh:mm A") : "-"}
                                </Descriptions.Item>
                                {note.collected_dt && (
                                    <>
                                        <Descriptions.Item label="Collected By">{note.collect_user_dname || "-"}</Descriptions.Item>
                                        <Descriptions.Item label="Collected Date">
                                            {dayjs(note.collected_dt).format("YYYY-MM-DD hh:mm A")}
                                        </Descriptions.Item>
                                    </>
                                )}
                            </Descriptions>
                        </Card>

                        <Card className="fluid-card" title={`Issued Items (${items.length})`}>
                            <Table
                                dataSource={items}
                                columns={columns}
                                rowKey="iinitem_id"
                                pagination={false}
                                bordered
                                size="middle"
                                scroll={{ x: 'max-content' }}
                            />
                        </Card>

                        <Card className="fluid-card" title={<><ClockCircleOutlined style={{ marginRight: 8 }} /> Approval History</>}>
                            <div style={{ padding: '24px 12px' }}>
                                <Timeline mode="left" items={getTimelineItems()} />
                            </div>
                        </Card>
                    </Space>
                )}
            </Content>

            <IssueNotePreviewModal
                iinId={id}
                visible={previewVisible}
                onCancel={() => setPreviewVisible(false)}
            />
            <Footerdiv />
        </Layout>
    );
};

export default ViewIssueNote;
